import {floatPriceFromFormatted} from '@optimaxdev/utils';
import {createSelector} from '@reduxjs/toolkit';

import {CartItemType, CartType} from 'desktop/pages/cart/types';

import {ApplicationStoreType} from 'desktop/store';
import {OrderReviewTotalsItemsType} from 'desktop/types/orderReviewTotalsType';
import {PayPalOrderDataType} from 'types/paypalType';

/**
 * Select Pay Pal Express data
 *
 * @param {ApplicationStoreType} store - current store
 */
export const selectPayPalExpress = (store: ApplicationStoreType) => store.cart.payPalExpress;

/**
 * Get is payment method loading
 *
 * @param {ApplicationStoreType} store - current store
 * @return {boolean} - payment method is loading
 */
export const getPayPalExpressLoading = createSelector(
    selectPayPalExpress,
    payPalExpress => payPalExpress.loading,
);

type CreateOrderLinesArgumentsType = {
    items: CartItemType[] | OrderReviewTotalsItemsType[];
    copay: string | number;
    shippingProtectionPrice?: number | null;
};

/**
 * Contains duplicated logic which creates order lines for paypal in the cart & checkout
 */
const createOrderLines = ({
    copay,
    items,
    shippingProtectionPrice,
}: CreateOrderLinesArgumentsType): PayPalOrderDataType['payment']['orderLines'] => {
    const orderLines: PayPalOrderDataType['payment']['orderLines'] = items.map(
        ({name, qty, total}) => ({
            name,
            quantity: Number(qty),
            unitPrice: Number(
                (
                    (floatPriceFromFormatted(total) * 100) /
                    (floatPriceFromFormatted(qty) * 100)
                ).toFixed(2),
            ),
        }),
    );

    if (shippingProtectionPrice) {
        orderLines.push({
            name: 'Shipping Protection',
            quantity: 1,
            unitPrice: Number(shippingProtectionPrice),
        });
    }

    if (Number(copay))
        // For UHC users we should create the additional item - co-pay and add it to subtotal
        // b/c subtotal should be equal to the sum of items costs
        orderLines.push({name: 'Co-pay', quantity: 1, unitPrice: Number(copay)});

    return orderLines;
};

/**
 * Get paypal order data (items, amount, tax amount) from cart store
 *
 * @param {ApplicationStoreType} store - current store
 */
export const getPayPalOrderDataFromCart = ({quoteItems, totals}: CartType): PayPalOrderDataType => {
    const orderLines = createOrderLines({
        copay: totals.copay,
        items: quoteItems.filter(({active}) => active),
        shippingProtectionPrice: totals.shippingProtectionPrice,
    });

    const paymentData: PayPalOrderDataType = {
        payment: {
            amount: Number(totals.total),
            discount: Number(totals.discount),
            orderLines,
            shipping: Number(totals.shippingPrice),
            shippingAddress: null,
            // we need to pass some shipping method, so backend will be able to use paypal replace operation
            shippingMethods: [
                {
                    additionalData: null,
                    code: 'unknown',
                    description: '',
                    discount: Number(totals.shippingDiscount),
                    price: Number(totals.shippingPrice),
                    selected: true,
                    // this title is visible under the blur
                    title: 'Processing...',
                },
            ],
            taxTotal: Number(totals.tax),
        },
    };

    return paymentData;
};
